import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import { MapPin, Route, Car, Navigation } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import toast from 'react-hot-toast';

// Fix para iconos de Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const Mapas: React.FC = () => {
  const [activeTab, setActiveTab] = useState('rutas');
  const [selectedRoute, setSelectedRoute] = useState<any>(null);

  // Datos de prueba para rutas
  const rutas = [
    {
      id: '1',
      nombre: 'Ruta 45 - Bogotá',
      numero: '45',
      operador: 'TransMilenio',
      estado: 'Activa',
      color: '#FF6B35',
      coordenadas: [
        [4.6097, -74.0817], // Bogotá centro
        [4.6500, -74.1000], // Norte
        [4.6200, -74.1200], // Noroccidente
        [4.5800, -74.1100], // Sur
        [4.6000, -74.0900]  // Regreso al centro
      ],
      paradas: [
        { nombre: 'Centro', coordenadas: [4.6097, -74.0817] },
        { nombre: 'Norte', coordenadas: [4.6500, -74.1000] },
        { nombre: 'Noroccidente', coordenadas: [4.6200, -74.1200] },
        { nombre: 'Sur', coordenadas: [4.5800, -74.1100] }
      ]
    },
    {
      id: '2',
      nombre: 'Ruta 12 - Medellín',
      numero: '12',
      operador: 'Metro de Medellín',
      estado: 'Activa',
      color: '#2ECC71',
      coordenadas: [
        [6.2442, -75.5812], // Medellín centro
        [6.2800, -75.6000], // Norte
        [6.2600, -75.6200], // Occidente
        [6.2200, -75.6100], // Sur
        [6.2400, -75.5900]  // Regreso al centro
      ],
      paradas: [
        { nombre: 'Centro', coordenadas: [6.2442, -75.5812] },
        { nombre: 'Norte', coordenadas: [6.2800, -75.6000] },
        { nombre: 'Occidente', coordenadas: [6.2600, -75.6200] },
        { nombre: 'Sur', coordenadas: [6.2200, -75.6100] }
      ]
    },
    {
      id: '3',
      nombre: 'Ruta 8 - Cali',
      numero: '8',
      operador: 'MIO',
      estado: 'Activa',
      color: '#3498DB',
      coordenadas: [
        [3.4516, -76.5320], // Cali centro
        [3.4800, -76.5500], // Norte
        [3.4600, -76.5700], // Occidente
        [3.4200, -76.5600], // Sur
        [3.4400, -76.5400]  // Regreso al centro
      ],
      paradas: [
        { nombre: 'Centro', coordenadas: [3.4516, -76.5320] },
        { nombre: 'Norte', coordenadas: [3.4800, -76.5500] },
        { nombre: 'Occidente', coordenadas: [3.4600, -76.5700] },
        { nombre: 'Sur', coordenadas: [3.4200, -76.5600] }
      ]
    }
  ];

  const servicios = [
    {
      id: '1',
      nombre: 'Estación de Servicio Norte',
      tipo: 'Gasolinera',
      coordenadas: [4.6500, -74.1000],
      estado: 'Activo'
    },
    {
      id: '2',
      nombre: 'Taller Mecánico Central',
      tipo: 'Taller',
      coordenadas: [4.6097, -74.0817],
      estado: 'Activo'
    },
    {
      id: '3',
      nombre: 'Centro de Revisión Técnica',
      tipo: 'Revisión',
      coordenadas: [4.6200, -74.1200],
      estado: 'Activo'
    }
  ];

  const getEstadoBadge = (estado: string) => {
    return estado === 'Activa' || estado === 'Activo' 
      ? 'bg-green-100 text-green-800' 
      : 'bg-red-100 text-red-800';
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'Gasolinera':
        return <Car className="w-4 h-4" />;
      case 'Taller':
        return <Car className="w-4 h-4" />;
      case 'Revisión':
        return <Car className="w-4 h-4" />;
      default:
        return <MapPin className="w-4 h-4" />;
    }
  };

  const handleRouteSelect = (ruta: any) => {
    setSelectedRoute(ruta);
    toast.success(`Ruta ${ruta.numero} seleccionada`);
  };

  const tabs = [
    { id: 'rutas', label: 'Rutas de Transporte', icon: Route },
    { id: 'servicios', label: 'Servicios', icon: MapPin },
    { id: 'estadisticas', label: 'Estadísticas', icon: Navigation }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mapas y Rutas</h1>
        <p className="text-gray-600">Visualiza rutas de transporte, servicios y análisis geográfico</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="w-4 h-4 inline mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          {activeTab === 'rutas' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Rutas de Transporte</h3>
              {rutas.map((ruta) => (
                <div
                  key={ruta.id}
                  className={`card p-4 cursor-pointer transition-colors ${
                    selectedRoute?.id === ruta.id ? 'ring-2 ring-primary-500' : ''
                  }`}
                  onClick={() => handleRouteSelect(ruta)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: ruta.color }}
                      ></div>
                      <h4 className="font-medium text-gray-900">Ruta {ruta.numero}</h4>
                    </div>
                    <span className={`badge ${getEstadoBadge(ruta.estado)}`}>
                      {ruta.estado}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{ruta.nombre}</p>
                  <p className="text-xs text-gray-500">Operador: {ruta.operador}</p>
                  <p className="text-xs text-gray-500">Paradas: {ruta.paradas.length}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'servicios' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Servicios</h3>
              {servicios.map((servicio) => (
                <div key={servicio.id} className="card p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      {getTipoIcon(servicio.tipo)}
                      <h4 className="font-medium text-gray-900">{servicio.nombre}</h4>
                    </div>
                    <span className={`badge ${getEstadoBadge(servicio.estado)}`}>
                      {servicio.estado}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{servicio.tipo}</p>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'estadisticas' && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Estadísticas</h3>
              <div className="card p-4">
                <h4 className="font-medium text-gray-900 mb-2">Rutas Activas</h4>
                <p className="text-2xl font-bold text-primary-600">{rutas.length}</p>
              </div>
              <div className="card p-4">
                <h4 className="font-medium text-gray-900 mb-2">Servicios</h4>
                <p className="text-2xl font-bold text-green-600">{servicios.length}</p>
              </div>
              <div className="card p-4">
                <h4 className="font-medium text-gray-900 mb-2">Cobertura</h4>
                <p className="text-2xl font-bold text-blue-600">85%</p>
              </div>
            </div>
          )}
        </div>

        {/* Map */}
        <div className="lg:col-span-2">
          <div className="card p-0 overflow-hidden">
            <div className="h-96">
              <MapContainer
                center={[4.6097, -74.0817]}
                zoom={11}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                
                {/* Rutas */}
                {rutas.map((ruta) => (
                  <Polyline
                    key={ruta.id}
                    positions={ruta.coordenadas as [number, number][]}
                    color={ruta.color}
                    weight={4}
                    opacity={selectedRoute?.id === ruta.id ? 1 : 0.6}
                  />
                ))}
                
                {/* Paradas */}
                {rutas.map((ruta) =>
                  ruta.paradas.map((parada, index) => (
                    <Marker key={`${ruta.id}-${index}`} position={parada.coordenadas as [number, number]}>
                      <Popup>
                        <div>
                          <h4 className="font-medium">{parada.nombre}</h4>
                          <p className="text-sm text-gray-600">Ruta {ruta.numero}</p>
                        </div>
                      </Popup>
                    </Marker>
                  ))
                )}
                
                {/* Servicios */}
                {servicios.map((servicio) => (
                  <Marker key={servicio.id} position={servicio.coordenadas as [number, number]}>
                    <Popup>
                      <div>
                        <h4 className="font-medium">{servicio.nombre}</h4>
                        <p className="text-sm text-gray-600">{servicio.tipo}</p>
                        <span className={`badge ${getEstadoBadge(servicio.estado)}`}>
                          {servicio.estado}
                        </span>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Route Details */}
      {selectedRoute && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Detalles de la Ruta {selectedRoute.numero}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Nombre</label>
              <p className="text-lg font-semibold text-gray-900">{selectedRoute.nombre}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Operador</label>
              <p className="text-lg font-semibold text-gray-900">{selectedRoute.operador}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Estado</label>
              <span className={`badge ${getEstadoBadge(selectedRoute.estado)}`}>
                {selectedRoute.estado}
              </span>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">Paradas</label>
              <p className="text-lg font-semibold text-gray-900">{selectedRoute.paradas.length}</p>
            </div>
          </div>
          
          <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-900 mb-2">Paradas</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {selectedRoute.paradas.map((parada: any, index: number) => (
                <div key={index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedRoute.color }}
                  ></div>
                  <span className="text-sm text-gray-700">{parada.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mapas;
